import React from "react";
import { TrendingUp, AlertTriangle, Minus } from "lucide-react";

// ---------------------------------------------------------------------------
// SectorVerdictPill — single source of truth for sector macro verdict
// styling across the entire app.
//
// Phase 11 polish (Tests 3/4/6/7/8/9 follow-up):
//   • OVERWEIGHT  → dark green filled (#15803d) + white bold text
//   • NEUTRAL     → blue filled (#1d4ed8) + white bold text
//                   (previously grey — too easy to confuse with "no data";
//                   blue makes the verdict obvious AND distinct from green/red)
//   • UNDERWEIGHT → red filled (#dc2626) + white bold text
//
// Both compact ("OVER"/"NEUT"/"UNDER") and expressive ("Sector: Overweight")
// labels are supported via the `compact` prop. The companion light-background
// "context box" colours are also exported so feature surfaces (Layer 6
// callout, MI tile, Sectors card, suggestion summary panel) can match
// without hardcoding values.
// ---------------------------------------------------------------------------

export const SECTOR_VERDICT_THEME = {
  OVERWEIGHT: {
    // Solid badge
    badgeBg:   "bg-[hsl(142_72%_29%)]",        // #15803d
    badgeText: "text-white",
    badgeRing: "ring-[hsl(142_72%_29%)]/0",
    // Light "context box" companion (page bg around prose)
    boxBg:     "bg-[hsl(142_72%_29%)]/10",     // ~ #f0fdf4 in dark/light
    boxBorder: "border-l-4 border-[hsl(142_72%_29%)]",
    boxText:   "text-[hsl(142_72%_22%)] dark:text-[hsl(142_72%_70%)]",
    Icon: TrendingUp,
    longLabel:  "Overweight",
    shortLabel: "OVER",
    tip: "Sector currently has favourable macro conditions",
  },
  NEUTRAL: {
    badgeBg:   "bg-[hsl(221_83%_47%)]",        // #1d4ed8
    badgeText: "text-white",
    badgeRing: "ring-[hsl(221_83%_47%)]/0",
    boxBg:     "bg-[hsl(221_83%_47%)]/10",
    boxBorder: "border-l-4 border-[hsl(221_83%_47%)]",
    boxText:   "text-[hsl(221_83%_36%)] dark:text-[hsl(221_83%_75%)]",
    Icon: Minus,
    longLabel:  "Neutral",
    shortLabel: "NEUT",
    tip: "Sector has balanced macro conditions",
  },
  UNDERWEIGHT: {
    badgeBg:   "bg-[hsl(0_72%_45%)]",          // #dc2626
    badgeText: "text-white",
    badgeRing: "ring-[hsl(0_72%_45%)]/0",
    boxBg:     "bg-[hsl(0_72%_45%)]/10",
    boxBorder: "border-l-4 border-[hsl(0_72%_45%)]",
    boxText:   "text-[hsl(0_72%_35%)] dark:text-[hsl(0_72%_75%)]",
    Icon: AlertTriangle,
    longLabel:  "Underweight",
    shortLabel: "UNDER",
    tip: "Sector currently has unfavourable macro conditions — consider smaller allocation",
  },
};

/**
 * Lookup helper — returns the full theme object for a verdict string,
 * defaulting to NEUTRAL when the value is missing/unknown.
 */
export const sectorVerdictTheme = (verdict) => {
  const key = (verdict || "").toString().toUpperCase();
  return SECTOR_VERDICT_THEME[key] || SECTOR_VERDICT_THEME.NEUTRAL;
};

const SectorVerdictPill = ({
  verdict,
  className = "",
  showIcon = true,
  /** size:
   *   "sm" — compact pill (filter chips, table rows)
   *   "md" — default (header, tile)
   *   "lg" — extra-large for hero surfaces */
  size = "md",
  /** compact = true → renders short label "OVER"/"NEUT"/"UNDER" instead
   * of the expressive "Sector: Overweight". Use in tight cells. */
  compact = false,
  testId,
}) => {
  const key = (verdict || "").toString().toUpperCase();
  const meta = SECTOR_VERDICT_THEME[key];
  if (!meta) return null;
  const Icon = meta.Icon;
  const sizeCls =
    size === "lg"
      ? "text-sm px-3 py-1"
      : size === "sm"
        ? "text-[10px] px-2 py-0.5"
        : "text-xs px-2.5 py-0.5";
  const label = compact ? meta.shortLabel : `Sector: ${meta.longLabel}`;
  return (
    <span
      data-testid={testId}
      title={meta.tip}
      className={`inline-flex items-center gap-1 rounded-full whitespace-nowrap font-bold tracking-wide shadow-sm ${sizeCls} ${meta.badgeBg} ${meta.badgeText} ${className}`}
    >
      {showIcon && <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />}
      {label}
    </span>
  );
};

export default SectorVerdictPill;
